/**
 * Shared presentation primitives for the consolidated Admin → Pricing screen.
 *
 * The screen is one long scrolling column of section cards with a sticky
 * "on this page" rail beside it, so these pieces exist to keep every section
 * looking identical: the same card head (title / description / version chip /
 * history button), the same formula banner, the same rail.
 *
 * Palette aliases `src/theme/colors.js` (the Eastview color system) so the page
 * reads as part of the admin section rather than carrying its own accent.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CheckRounded from "@mui/icons-material/CheckRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import KeyboardArrowUpRounded from "@mui/icons-material/KeyboardArrowUpRounded";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import AdminField from "../shared/AdminField";
import { FIELD_TEXT } from "../../../theme/formStyles";
import { A, STICKY_TABS_H } from "../shared/adminTokens";
import { ink, level1, slate, status, teal } from "../../../theme/colors";

export const T = {
  ink,
  ink2: slate[700],
  muted: slate[500],
  faint: slate[400],
  line: "#e5e7eb",
  lineSoft: "#eef2f6",
  lineStrong: slate[300],
  canvas: slate[50],
  surface: "#ffffff",
  surface2: slate[50],
  // The admin section's selection accent (the rails here and on
  // AdminGuidelinesScreen). This is a level-1 "place" active state, so it uses
  // the color system's active-text / tint pair rather than a screen-local blue.
  accent: level1.text,
  accentInk: teal[800],
  accentSoft: teal[50],
  accentSoft2: level1.tint,
  warn: status.warning.fg,
  warnSoft: status.warning.bg,
  warnLine: "#fde68a",
  mono: 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
  sans: "Inter, sans-serif",
};

/**
 * Air above a section once it has been scrolled to, and above the sticky rail.
 * Both also have to clear AdminLayout's pinned sub-tab strip, so the real offset
 * is `STICKY_TABS_H + SCROLL_OFFSET`.
 */
export const SCROLL_OFFSET = 16;
const ANCHOR_OFFSET = STICKY_TABS_H + SCROLL_OFFSET;

/* ────────────────────────────── Formula banner ───────────────────────────── */

/**
 * The "how this page composes a number" strip that opens each tab. `parts` is a
 * list of `{ chip }` / `{ op }` / `{ text }` tokens rendered left to right.
 */
export const FormulaBanner = ({ lead, parts, tail }) => (
  <Flx
    ac
    wrap
    g={1}
    sx={{
      bgcolor: T.surface,
      border: `1px solid ${T.line}`,
      borderRadius: "10px",
      px: 2,
      py: 1.5,
      fontSize: 12.5,
      color: T.muted,
    }}
  >
    {lead ? <Txt sx={{ fontSize: 12.5, color: T.muted }}>{lead}</Txt> : null}
    {parts.map((p, i) => {
      if (p.op)
        return (
          <Txt
            key={i}
            sx={{ fontSize: 12.5, fontWeight: 700, color: T.faint }}
          >
            {p.op}
          </Txt>
        );
      if (p.text)
        return (
          <Txt key={i} sx={{ fontSize: 12, color: T.muted }}>
            {p.text}
          </Txt>
        );
      return (
        <Box
          key={i}
          sx={{
            fontFamily: T.mono,
            fontSize: 11.5,
            fontWeight: 500,
            color: T.ink2,
            bgcolor: T.surface2,
            border: `1px solid ${T.line}`,
            borderRadius: "6px",
            px: 1,
            py: 0.25,
            whiteSpace: "nowrap",
          }}
        >
          {p.chip}
        </Box>
      );
    })}
    {tail ? (
      <Txt sx={{ fontSize: 12, fontWeight: 600, color: T.accent }}>{tail}</Txt>
    ) : null}
  </Flx>
);

FormulaBanner.propTypes = {
  lead: PropTypes.string,
  parts: PropTypes.arrayOf(PropTypes.object).isRequired,
  tail: PropTypes.string,
};

/* ────────────────────────────────── Fields ───────────────────────────────── */

/**
 * This screen's form field is the section-wide one — kept as a local name only
 * because every call site on this screen already uses it. New code can import
 * `AdminField` directly.
 */
export const PricingField = AdminField;

/**
 * PricingField with the 2026-10-08 field label / helper standard (FIELD_TEXT:
 * 12.5px #111827 label 4px above, 11.5px #64748B helper). Used by the correspondent
 * Pricing tab cards; new pricing forms use this rather than PricingField.
 */
export const PricingFieldText = (props) => (
  <AdminField labelSx={FIELD_TEXT.label} helperSx={FIELD_TEXT.helper} {...props} />
);

/* ─────────────────────────────── Section card ────────────────────────────── */

/** Small mono pill used for version numbers and other terse metadata. */
export const MetaChip = ({ children, tone }) => (
  <Box
    sx={{
      fontFamily: T.mono,
      fontSize: 10.5,
      fontWeight: 500,
      color: tone === "warn" ? T.warn : T.muted,
      bgcolor: tone === "warn" ? T.warnSoft : T.surface2,
      border: `1px solid ${tone === "warn" ? T.warnLine : T.line}`,
      borderRadius: "5px",
      px: 0.875,
      py: 0.125,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </Box>
);

MetaChip.propTypes = {
  children: PropTypes.node,
  tone: PropTypes.oneOf(["warn"]),
};

/**
 * The section's most recent edit, inline under its description —
 * admin-rtl-pricing-config.md: "every section save / tier upsert is
 * version-history tracked — surface the LAST entry inline on each section
 * header". `edit` is `{ at, by, note?, detail? }`; renders nothing without
 * `at`. Timestamps render in Eastern via formatDateTime.
 */
export const LastEditLine = ({ edit }) => {
  if (!edit?.at) return null;
  const when = formatDateTime(edit.at, "datetime");
  const note = String(edit.note || "").trim();
  return (
    <Txt
      component="div"
      sx={{
        fontSize: 11.5,
        color: T.faint,
        lineHeight: 1.5,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: 720,
      }}
      title={note ? `${when} · ${edit.by || "unknown"} — ${note}` : undefined}
    >
      Last edit {when || "—"} ·{" "}
      <Box component="span" sx={{ color: T.muted, fontWeight: 500 }}>
        {edit.by || "unknown user"}
      </Box>
      {edit.detail ? ` · ${edit.detail}` : ""}
      {note ? ` — “${note}”` : ""}
    </Txt>
  );
};

LastEditLine.propTypes = {
  edit: PropTypes.shape({
    at: PropTypes.any,
    by: PropTypes.string,
    note: PropTypes.string,
    detail: PropTypes.node,
  }),
};

/* ───────────── last change from a row's append-only versionHistory ────────── */

/**
 * The `/correspondents/admin/*` tables (tier margins, RTL tier spreads, RTL base
 * rates) return each row WITH its append-only `versionHistory` (oldest first):
 * `{ changedBy, changedDateTime, priorEffectiveDate, changes? | priorRatesByTerm? }`.
 * These helpers surface the last entry inline, per row and per section
 * (correspondent-admin-and-locks.md: "show the last entry inline").
 */
export const lastHistoryEntry = (row) => {
  const h = Array.isArray(row?.versionHistory) ? row.versionHistory : [];
  return h.length ? h[h.length - 1] : null;
};

/** The newest entry across a table's rows, shaped for `PricingSection.lastEdit`. */
export const newestHistoryEdit = (rows, detailOf) => {
  let best = null;
  let bestRow = null;
  (rows || []).forEach((row) => {
    const e = lastHistoryEntry(row);
    if (e?.changedDateTime && (!best || String(e.changedDateTime) > String(best.changedDateTime))) {
      best = e;
      bestRow = row;
    }
  });
  return best
    ? { at: best.changedDateTime, by: best.changedBy, detail: detailOf?.(bestRow) }
    : null;
};

const fmtHistoryValue = (v) =>
  v == null || v === "" ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v);

/** The field-level lines of one history entry, for a tooltip. */
const describeHistoryEntry = (e, formatPrior) => {
  if (!e) return null;
  if (e.changes && Object.keys(e.changes).length)
    return Object.entries(e.changes)
      .map(([k, d]) => `${k}: ${fmtHistoryValue(d?.old)} → ${fmtHistoryValue(d?.new)}`)
      .join("\n");
  if (e.priorRatesByTerm) return formatPrior ? formatPrior(e.priorRatesByTerm) : null;
  return null;
};

/**
 * AG Grid column: "Last change" = `date · who` from the row's newest history
 * entry, what moved in the tooltip. `entryOf(data)` picks the entry (defaults to
 * the row's own versionHistory); `formatPrior` renders a base-rate
 * `priorRatesByTerm` for the tooltip.
 */
export const lastChangeColumn = ({ entryOf = lastHistoryEntry, formatPrior } = {}) => ({
  headerName: "Last change",
  colId: "lastChange",
  minWidth: 210,
  flex: 1,
  valueGetter: (p) => entryOf(p.data)?.changedDateTime || null,
  valueFormatter: (p) => {
    const e = entryOf(p.data);
    if (!e) return "—";
    return `${formatDateTime(e.changedDateTime, "date") || "—"} · ${e.changedBy || "unknown"}`;
  },
  tooltipValueGetter: (p) => describeHistoryEntry(entryOf(p.data), formatPrior),
});

/** In-progress ring: a slate track with the done share drawn in teal. */
const ProgressRing = ({ value, size = 18 }) => (
  <Box
    component="span"
    aria-label="In progress"
    sx={{ position: "relative", width: size, height: size, flexShrink: 0, display: "inline-flex" }}
  >
    <CircularProgress
      variant="determinate"
      value={100}
      size={size}
      thickness={5}
      sx={{ color: slate[200], position: "absolute", inset: 0 }}
    />
    {value > 0 ? (
      <CircularProgress
        variant="determinate"
        value={Math.max(value, 4)}
        size={size}
        thickness={5}
        sx={{ color: teal[600], position: "absolute", inset: 0 }}
      />
    ) : null}
  </Box>
);

/**
 * A section's status mark — the green check when `complete`, else the
 * progress ring for `{ done, total }`. The section header and the "On this
 * page" rail both draw it, so the two always agree. Renders nothing when the
 * section reports neither.
 */
export const SectionStatusIcon = ({ complete, progress, size = 18 }) => {
  if (complete)
    return (
      <Tooltip title="Complete">
        <CheckRounded
          aria-label="Complete"
          sx={{ fontSize: size + 2, flexShrink: 0, color: status.success.solid }}
        />
      </Tooltip>
    );
  if (progress?.total)
    return (
      <Tooltip title={`${progress.done} of ${progress.total} done`}>
        <ProgressRing value={(progress.done / progress.total) * 100} size={size} />
      </Tooltip>
    );
  return null;
};

/**
 * A section of the pricing page. `id` doubles as the scroll anchor the rail
 * links to, so it must match the rail item's id.
 */
export const PricingSection = ({
  id,
  title,
  desc,
  version,
  // `{ at, by, note? }` — the section's last edit, shown under the description.
  lastEdit,
  onHistory,
  historyLabel,
  actions,
  // A status pill beside the chevron — unlike `actions`, it stays visible
  // while the card is collapsed, so a folded card still says where it stands.
  badge,
  // Draws a green check before the title — the section has everything it asks for.
  complete,
  // `{ done, total }` — while not complete, an in-progress ring before the
  // title and "done of total" beside the chevron.
  progress,
  // Puts `actions` on their own full-width row under the description. Use it
  // whenever the control is wide (a product tab strip, a row of buttons) —
  // inline, it squeezes the title and description into a narrow column.
  stackActions,
  collapsible = true,
  defaultOpen = true,
  dense,
  // Gap between the header block (title + description) and the body. The
  // pricing screens keep the tight default; a form-heavy card wants more.
  headerGap = 1.5,
  children,
  sx,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const canCollapse = collapsible && !!title;

  return (
    <Box
      id={id}
      sx={{
        bgcolor: T.surface,
        border: `1px solid ${T.line}`,
        borderRadius: "10px",
        scrollMarginTop: `${ANCHOR_OFFSET}px`,
        transition: "box-shadow .2s",
        "&:hover": { boxShadow: "0 4px 16px -2px rgba(22,33,46,.08)" },
        ...sx,
      }}
    >
      {title ? (
        <Flx column g={1.5} sx={{ px: 2.5, pt: 2, pb: open ? headerGap : 2 }}>
          <Flx g={1.5} wrap sx={{ alignItems: "flex-start" }}>
            <Flx
              column
              g={0.25}
              sx={{
                minWidth: 0,
                flex: 1,
                cursor: canCollapse ? "pointer" : "default",
              }}
              onClick={canCollapse ? () => setOpen((o) => !o) : undefined}
            >
              <Flx ac g={1}>
                <SectionStatusIcon complete={complete} progress={progress} />
                <Txt sx={{ fontSize: 14.5, fontWeight: 600, color: T.ink }}>
                  {title}
                </Txt>
              </Flx>
              {desc ? (
                <Txt
                  // No max width — a section description runs the full
                  // width of its card (CLAUDE.md "no max width on descriptions").
                  sx={{
                    fontSize: 12,
                    color: T.muted,
                    lineHeight: 1.5,
                  }}
                >
                  {desc}
                </Txt>
              ) : null}
              <LastEditLine edit={lastEdit} />
            </Flx>
            {/* Controls live opposite the title; the collapse chevron anchors
                the far right so it lines up down the column of cards. */}
            <Flx ac g={1} wrap sx={{ flexShrink: 0 }}>
              {stackActions || !open ? null : actions}
              {badge || null}
              {!complete && progress?.total ? (
                <Txt sx={{ fontSize: 12, color: T.muted, whiteSpace: "nowrap" }}>
                  {progress.done} of {progress.total}
                </Txt>
              ) : null}
              {version ? <MetaChip>{version}</MetaChip> : null}
              {onHistory ? (
                <Tooltip title={historyLabel || "Version history"}>
                  <IconButton
                    size="small"
                    onClick={onHistory}
                    sx={{ color: T.faint, "&:hover": { color: T.ink } }}
                  >
                    <HistoryRounded sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              ) : null}
              {canCollapse ? (
                <Tooltip title={open ? "Collapse" : "Expand"}>
                  <IconButton
                    size="small"
                    onClick={() => setOpen((o) => !o)}
                    sx={{ color: T.faint, "&:hover": { color: T.ink } }}
                  >
                    <ChevronRightRounded
                      sx={{
                        fontSize: 20,
                        transition: "transform .18s",
                        transform: open ? "rotate(90deg)" : "none",
                      }}
                    />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Flx>
          </Flx>
          {stackActions && actions && open ? (
            <Flx ac g={1} wrap sx={{ minWidth: 0 }}>
              {actions}
            </Flx>
          ) : null}
        </Flx>
      ) : null}

      {open ? (
        <Box
          sx={{ px: dense ? 0 : 2.5, pb: dense ? 0 : 2.25, pt: title ? 0 : 2 }}
        >
          {children}
        </Box>
      ) : null}
    </Box>
  );
};

PricingSection.propTypes = {
  id: PropTypes.string,
  title: PropTypes.node,
  desc: PropTypes.node,
  version: PropTypes.node,
  lastEdit: PropTypes.object,
  onHistory: PropTypes.func,
  historyLabel: PropTypes.string,
  actions: PropTypes.node,
  stackActions: PropTypes.bool,
  collapsible: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  dense: PropTypes.bool,
  headerGap: PropTypes.number,
  children: PropTypes.node,
  sx: PropTypes.object,
};

/**
 * A collapsible block *inside* a section — one LLPA matrix, say, where a card
 * holds several tables and you want to fold away the ones you aren't editing.
 */
export const PricingSubSection = ({
  title,
  meta,
  defaultOpen = true,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Box>
      {/* Chevron leads here, unlike the card header — at this level it reads as
          a disclosure triangle on a nested item rather than a card control.
          No rule between siblings: the caller separates them with gap instead,
          so the stack reads as blocks of air rather than ruled-off rows. */}
      <Flx
        ac
        g={1}
        onClick={() => setOpen((o) => !o)}
        sx={{
          px: 2.5,
          py: 1.25,
          cursor: "pointer",
          userSelect: "none",
          "&:hover": { bgcolor: T.surface2 },
        }}
      >
        <ChevronRightRounded
          sx={{
            fontSize: 18,
            color: T.faint,
            flexShrink: 0,
            transition: "transform .18s",
            transform: open ? "rotate(90deg)" : "none",
          }}
        />
        <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>
          {title}
        </Txt>
        {meta ? <MetaChip>{meta}</MetaChip> : null}
      </Flx>
      {open ? <Box sx={{ px: 2.5, pb: 2 }}>{children}</Box> : null}
    </Box>
  );
};

PricingSubSection.propTypes = {
  title: PropTypes.node,
  meta: PropTypes.node,
  defaultOpen: PropTypes.bool,
  children: PropTypes.node,
};

/* ─────────────────────────────────── Grids ───────────────────────────────── */

// The table system now lives in `shared/adminGrid.js` — Guidelines needed the
// same tables, and ADMIN_UI.md says a pricing primitive moves to shared the
// moment a second screen wants it. Re-exported under the old names so the
// pricing call sites are untouched and there is one definition of the look.
export {
  GRID_HEADER_H,
  GRID_ROW_H,
  gridHeight,
  Expander,
  ExpandableBlock,
  ADMIN_GRID_SX as PRICING_GRID_SX,
  ADMIN_GRID_COL_DEF as PRICING_GRID_COL_DEF,
  AdminGrid as PricingGrid,
} from '../shared/adminGrid';


/** Muted placeholder inside a section. */
export const SectionEmpty = ({ children }) => (
  <Txt
    sx={{
      fontSize: 12.5,
      color: T.faint,
      display: "block",
      textAlign: "center",
      py: 3,
    }}
  >
    {children}
  </Txt>
);

SectionEmpty.propTypes = { children: PropTypes.node };

/* ──────────────────────────── "On this page" rail ────────────────────────── */

/** Nearest ancestor that actually scrolls, or null for the document. */
const scrollParentOf = (el) => {
  let node = el?.parentElement;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") return node;
    node = node.parentElement;
  }
  return null;
};

/**
 * Scroll-spy anchor nav.
 *
 * Which element scrolls is not this component's business — the screen may own
 * its scroll container or defer to the dashboard's <main> — so it walks up from
 * the first section to find whichever ancestor does, and listens there.
 */
export const SectionRail = ({ items, itemPy = 1.25 }) => {
  const [active, setActive] = useState(items[0]?.id);
  // Any item carrying a `status` gives the whole rail an icon column.
  const withStatus = items.some((it) => it.status);
  // Suppresses spy updates while a click-triggered smooth scroll is in flight,
  // otherwise the intermediate sections steal the highlight on the way past.
  const lockRef = useRef(0);

  useEffect(() => {
    const first = document.getElementById(items[0]?.id);
    const root = scrollParentOf(first);
    const target = root || window;

    const onScroll = () => {
      if (Date.now() < lockRef.current) return;
      const rootTop = root ? root.getBoundingClientRect().top : 0;
      let current = items[0]?.id;
      items.forEach((it) => {
        const el = document.getElementById(it.id);
        if (!el) return;
        // A section claims the highlight once its top passes just under the
        // container's top edge.
        if (el.getBoundingClientRect().top - rootTop <= ANCHOR_OFFSET + 24) {
          current = it.id;
        }
      });
      setActive(current);
    };

    onScroll();
    target.addEventListener("scroll", onScroll, { passive: true });
    return () => target.removeEventListener("scroll", onScroll);
  }, [items]);

  const go = useCallback(
    (id) => {
      const el = document.getElementById(id);
      if (!el) return;
      lockRef.current = Date.now() + 700;
      setActive(id);
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [],
  );

  const toTop = useCallback(() => {
    const first = document.getElementById(items[0]?.id);
    const root = scrollParentOf(first);
    (root || window).scrollTo({ top: 0, behavior: "smooth" });
  }, [items]);

  return (
    // Clears the sticky sub-tab strip AdminLayout pins at the top of the scroll
    // container, plus a little air.
    <Box sx={{ position: "sticky", top: ANCHOR_OFFSET }}>
      <Txt
        sx={{
          display: "block",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: ".1em",
          textTransform: "uppercase",
          color: A.faint,
          px: 1.5,
          pb: 1,
        }}
      >
        On this page
      </Txt>
      {/* A hairline spine with the active item's marker riding it — the same
          shape as the sidebar's active rule, one level in. */}
      <Box
        component="nav"
        sx={{ display: "flex", flexDirection: "column", borderLeft: `2px solid ${A.line}` }}
      >
        {items.map((it) => {
          const isActive = active === it.id;
          return (
            <Box
              key={it.id}
              onClick={() => go(it.id)}
              sx={{
                display: "flex",
                alignItems: withStatus ? "center" : "baseline",
                gap: 0.75,
                px: 1.5,
                py: itemPy,
                ml: "-2px",
                borderLeft: `2px solid ${isActive ? A.teal : "transparent"}`,
                cursor: "pointer",
                fontSize: 12.5,
                fontWeight: isActive ? 600 : 500,
                color: isActive ? A.tealDeep : A.muted,
                userSelect: "none",
                transition: "color .13s, border-color .13s",
                "&:hover": { color: isActive ? A.tealDeep : A.ink },
              }}
            >
              {/* The section's own check / progress ring, when the page
                  passes one; a blank slot of the same width keeps every
                  label on one left edge. */}
              {withStatus ? (
                <Box
                  component="span"
                  sx={{ width: 16, flexShrink: 0, display: "inline-flex", justifyContent: "center" }}
                >
                  {it.status ? (
                    <SectionStatusIcon
                      complete={it.status.complete}
                      progress={it.status.progress}
                      size={13}
                    />
                  ) : null}
                </Box>
              ) : null}
              <Box component="span" sx={{ minWidth: 0 }}>
                {it.label}
              </Box>
              {it.meta ? (
                <Box
                  component="span"
                  sx={{
                    ml: "auto",
                    fontFamily: A.mono,
                    fontSize: 9.5,
                    color: A.faint,
                    flexShrink: 0,
                  }}
                >
                  {it.meta}
                </Box>
              ) : null}
            </Box>
          );
        })}
      </Box>
      <Box
        component="button"
        type="button"
        onClick={toTop}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.625,
          mt: 1.5,
          px: 1.5,
          py: 0.625,
          border: "none",
          background: "none",
          cursor: "pointer",
          font: "inherit",
          fontSize: 11.5,
          fontWeight: 600,
          color: A.faint,
          borderRadius: "6px",
          "&:hover": { color: A.tealDeep, bgcolor: A.tealSoft },
        }}
      >
        <KeyboardArrowUpRounded sx={{ fontSize: 14 }} />
        Back to top
      </Box>
    </Box>
  );
};

SectionRail.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.node,
      // Terse right-aligned annotation — the section's version, typically.
      meta: PropTypes.node,
      // `{ complete, progress: { done, total } }` — the same status the
      // section's header shows, drawn left of the label.
      status: PropTypes.object,
    }),
  ).isRequired,
};

/* ───────────────────────────────── Page layout ───────────────────────────── */

/**
 * content | "on this page" | optional aside. The anchor nav sits to the RIGHT of
 * the content, where a page's own table of contents belongs now that the section
 * nav owns the left edge of the screen. Narrow windows drop the aside first,
 * then the anchor nav, so the content column is the last thing to give.
 */
export const PricingPageLayout = ({ nav, children, aside, railItemPy }) => {
  const cols = useMemo(
    () =>
      aside
        ? {
            xs: "minmax(0,1fr)",
            lg: "minmax(0,1fr) 320px",
            xl: "minmax(0,1fr) 320px 190px",
          }
        // 190px rail: room for a status icon beside the longest labels.
        : { xs: "minmax(0,1fr)", lg: "minmax(0,1fr) 190px" },
    [aside],
  );

  return (
    // No `alignItems: start` here, deliberately. Grid items default to
    // `stretch`, which makes the side columns as tall as the content column —
    // and a sticky child can only travel within its own containing block, so
    // with `start` the rail and preview would scroll away after a few hundred
    // pixels instead of following the page.
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: cols,
        gap: 3,
      }}
    >
      <Flx column g={2.5} sx={{ minWidth: 0 }}>
        {children}
      </Flx>
      {aside ? (
        <Box sx={{ display: { xs: "none", lg: "block" } }}>
          <Box sx={{ position: "sticky", top: ANCHOR_OFFSET }}>
            {aside}
          </Box>
        </Box>
      ) : null}
      <Box
        sx={{
          display: aside
            ? { xs: "none", xl: "block" }
            : { xs: "none", lg: "block" },
        }}
      >
        {nav ? <SectionRail items={nav} itemPy={railItemPy} /> : null}
      </Box>
    </Box>
  );
};

PricingPageLayout.propTypes = {
  nav: PropTypes.array,
  children: PropTypes.node,
  aside: PropTypes.node,
  // Per-item vertical padding on the "On this page" rail. Every rail uses the
  // airy default (set by the correspondent Profile tab); pass a value only for
  // a rail that genuinely needs to differ.
  railItemPy: PropTypes.number,
};
